import * as t from 'io-ts';
import _, {forEach, isArray, isObject} from 'lodash';

import {hasProperty} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {onlyItem} from '../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {RefType} from '../../models/api/apiTypes';
import {AppState, RAppState} from '../../store/appState';
import {ResourceMapAffinitiesEnum, ResourceMapType} from '../../store/appTypes';
import {doesTypeContainRefMemoized, extractValueAtPath, findResourceMapValueType} from './gcHelpers';
import {
  GarbageCollectorCleaningState,
  GarbageCollectorSearchingState,
  GarbageCollectorStagesEnum,
  ReachableChild,
  ResourceMapDefinition,
} from './gcTypes';

/*
 * Phase #2: Search for reachable items.
 */

/** Performs one cycle of searching for reachable items. */
export function searchForReachableItems(
  gcState: GarbageCollectorSearchingState,
  appState: AppState,
  shouldKeepCycles: boolean,
): GarbageCollectorSearchingState | GarbageCollectorCleaningState {
  const {mapDefinitions, removalCandidates, unscannedItems, scannedItems, lastScannedState} = gcState;

  const reachableChild = unscannedItems.pop();
  if (!reachableChild) {
    // We've finished searching for refs. If the app state has changed in the meantime, we'll need to search
    // the changes for the refs. If not, we can move to the cleaning stage.
    return searchForChangedResources(gcState, appState);
  }

  const {obj, type, path, isTransient} = reachableChild;
  if (scannedItems.has(obj, type)) {
    // We've already scanned this item, so we're done.
    return gcState;
  }

  scannedItems.add(obj, type);
  findReachableChildren(
    path,
    obj,
    type,
    isTransient,
    lastScannedState,
    mapDefinitions,
    shouldKeepCycles,
  ).forEach((child) => {
    if (!child.isTransient) {
      removalCandidates.delete(child.path);
    }

    unscannedItems.push(child);
  });

  return gcState;
}

/*
 * Helpers.
 */

function findReachableChildren(
  path: ReadonlyArray<string>,
  obj: unknown,
  type: t.Type<any>,
  isTransient: boolean,
  appState: AppState,
  mapDefinitions: ReadonlyArray<ResourceMapDefinition>,
  shouldKeepCycles: boolean,
): ReadonlyArray<ReachableChild> {
  // We don't need to search children of transient resources unless we need to keep cycles.
  if (isTransient && !shouldKeepCycles) {
    return [];
  }

  // If we know this type doesn't contain any refs, we can stop searching early.
  if (!doesTypeContainRefMemoized(type)) {
    return [];
  }

  if (obj === undefined || obj === null) {
    return [];
  }

  // Resource map.
  if (type instanceof ResourceMapType && isObject(obj)) {
    return Object.entries(obj).map(([key, value]) => ({
      obj: value,
      path: [...path, key],
      type: findResourceMapValueType(type),
      isTransient: type.getAffinity(appState) === ResourceMapAffinitiesEnum.TRANSIENT,
    }));
  }

  // RefType.
  if (type instanceof RefType) {
    const resourceMapDefinition = onlyItem(
      mapDefinitions.filter((definition) => findResourceMapValueType(definition.type) === type.targetType),
    );
    if (!resourceMapDefinition) {
      throw new Error('Unable to find a resource map for ref.');
    }

    const resourcePath = [...resourceMapDefinition.path, `${obj}`];
    return [
      {
        obj: extractValueAtPath(resourcePath, appState),
        path: resourcePath,
        type: type.targetType,
        // If we need to keep cycles, treat ref objects as non-transient. If not, use the parent value.
        isTransient: isTransient && !shouldKeepCycles,
      },
    ];
  }

  // Undefined/null type.
  if (type instanceof t.UndefinedType || type instanceof t.NullType || type instanceof t.UnknownType) {
    return [];
  }

  // Primitives.
  if (
    type instanceof t.StringType ||
    type instanceof t.NumberType ||
    type instanceof t.LiteralType ||
    type instanceof t.BooleanType
  ) {
    return [];
  }

  // Wrapper types.
  if (type instanceof t.RefinementType || type instanceof t.ReadonlyType) {
    return [
      {
        obj,
        path,
        type: type.type,
        isTransient,
      },
    ];
  }

  // Array types.
  if (type instanceof t.ArrayType || type instanceof t.ReadonlyArrayType) {
    if (!isArray(obj)) {
      return [];
    }

    return obj.map((item, index) => ({
      obj: item,
      path: [...path, index.toString()],
      type: type.type,
      isTransient,
    }));
  }

  // Dictionary type.
  if (type instanceof t.DictionaryType && isObject(obj)) {
    return Object.entries(obj).map(([key, value]) => ({
      obj: value,
      path: [...path, key],
      type: type.codomain,
      isTransient,
    }));
  }

  // Tagged union type.
  if (type instanceof t.TaggedUnionType) {
    return type.types.map((subType: t.Type<any>) => ({obj, path, type: subType, isTransient}));
  }

  // Union / Intersection types.
  if (type instanceof t.UnionType || type instanceof t.IntersectionType) {
    return type.types.map((subType: t.Type<any>) => ({obj, path, type: subType, isTransient}));
  }

  // Interface type.
  if (type instanceof t.InterfaceType || type instanceof t.PartialType) {
    return Object.keys(type.props).map((key) => {
      const propType: t.Type<any> = type.props[key];
      const value = hasProperty(obj, key) ? obj[key] : undefined;
      return {
        obj: value,
        path: [...path, key],
        type: propType,
        isTransient,
      };
    });
  }

  return [];
}

function searchForChangedResources(
  gcState: GarbageCollectorSearchingState,
  appState: AppState,
): GarbageCollectorSearchingState | GarbageCollectorCleaningState {
  const {mapDefinitions, removalCandidates, scannedItems} = gcState;

  const unscannedItems: Array<ReachableChild> = [];
  computeStateChanges(gcState.lastScannedState, appState, RAppState, [], unscannedItems);

  if (unscannedItems.length === 0) {
    // We didn't find any new resources, so we can move to the cleaning state.
    return {
      stage: GarbageCollectorStagesEnum.CLEANING,
      mapDefinitions,
      removalCandidates,
    };
  }

  // We have to stay in the searching state and scan for refs in the changed items.
  return {
    stage: GarbageCollectorStagesEnum.SEARCHING,
    mapDefinitions,
    removalCandidates,
    unscannedItems,
    scannedItems,
    lastScannedState: appState,
  };
}

function computeStateChanges(
  before: unknown,
  after: unknown,
  type: t.Type<any>,
  path: ReadonlyArray<string>,
  newResources: Array<ReachableChild>,
) {
  // The objects are identical. No need to keep searching.
  if (before === after) {
    return;
  }

  // The object no longer exists.
  if (after === undefined) {
    return;
  }

  // The object didn't exist before.
  if (before === undefined) {
    newResources.push({
      obj: after,
      path,
      type,
      isTransient: false,
    });
    return;
  }

  // Resource map.
  if (type instanceof ResourceMapType && isObject(after)) {
    _(after)
      .keys()
      .forEach((key) => {
        const afterObject = hasProperty(after, key) ? after[key] : undefined;
        const beforeObject = hasProperty(before, key) ? before[key] : undefined;
        if (afterObject !== beforeObject) {
          computeStateChanges(
            beforeObject,
            afterObject,
            findResourceMapValueType(type),
            [...path, key],
            newResources,
          );
        }
      });
    return;
  }

  // RefType.
  if (type instanceof RefType) {
    newResources.push({
      obj: after,
      path,
      type,
      isTransient: false,
    });
    return;
  }

  // Undefined/null type.
  if (type instanceof t.UndefinedType || type instanceof t.NullType || type instanceof t.UnknownType) {
    return;
  }

  // Primitives.
  if (
    type instanceof t.StringType ||
    type instanceof t.NumberType ||
    type instanceof t.LiteralType ||
    type instanceof t.BooleanType
  ) {
    return;
  }

  // Wrapper types.
  if (type instanceof t.RefinementType || type instanceof t.ReadonlyType) {
    computeStateChanges(before, after, type.type, path, newResources);
    return;
  }

  // Array types.
  if ((type instanceof t.ArrayType || type instanceof t.ReadonlyArrayType) && isArray(after)) {
    const beforeSet = new Set(isArray(before) ? before : []);
    after.forEach((value, index) => {
      if (beforeSet.has(value)) {
        // This object is not new.
        return;
      }

      computeStateChanges(undefined, value, type.type, [...path, `${index}`], newResources);
    });
    return;
  }

  // Dictionary type.
  if (type instanceof t.DictionaryType && isObject(after)) {
    _(after)
      .keys()
      .forEach((key) => {
        const afterObject = hasProperty(after, key) ? after[key] : undefined;
        const beforeObject = hasProperty(before, key) ? before[key] : undefined;
        if (afterObject !== beforeObject) {
          computeStateChanges(beforeObject, afterObject, type.codomain, [...path, key], newResources);
        }
      });
    return;
  }

  // Tagged union type.
  if (type instanceof t.TaggedUnionType) {
    forEach(type.types, (subType: t.Type<any>) =>
      computeStateChanges(before, after, subType, path, newResources),
    );
    return;
  }

  // Union / Intersection types.
  if (type instanceof t.UnionType || type instanceof t.IntersectionType) {
    forEach(type.types, (subType: t.Type<any>) =>
      computeStateChanges(before, after, subType, path, newResources),
    );
    return;
  }

  // Interface type.
  if (type instanceof t.InterfaceType || type instanceof t.PartialType) {
    forEach(type.props, (propType: t.Type<any>, key) => {
      const afterObject = hasProperty(after, key) ? after[key] : undefined;
      const beforeObject = hasProperty(before, key) ? before[key] : undefined;
      if (afterObject !== beforeObject) {
        computeStateChanges(beforeObject, afterObject, propType, [...path, key], newResources);
      }
    });
  }
}
